import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    format: String,
    filename: String,
    includeMetadata: Boolean
  }

  connect() {
    this.formatValue = this.formatValue || 'csv'
    this.filenameValue = this.filenameValue || 'chart-data'
    this.includeMetadataValue = this.includeMetadataValue !== false
  }

  download(event) {
    event.preventDefault()

    // Find the nearest chart canvas element
    const chartCanvas = this.findNearestChart()

    if (!chartCanvas) {
      alert('Kein Diagramm gefunden. Bitte platzieren Sie den Download-Button in der Nähe eines Diagramms.')
      return
    }

    // Extract chart data
    const chartData = this.extractChartData(chartCanvas)

    if (!chartData) {
      alert('Diagrammdaten konnten nicht extrahiert werden.')
      return
    }

    // Generate and download file based on format
    if (this.formatValue === 'csv') {
      this.downloadCSV(chartData)
    } else if (this.formatValue === 'json') {
      this.downloadJSON(chartData)
    }
  }

  findNearestChart() {
    // The button controller is attached to the <button> element, but the button
    // is wrapped in a div. We need to search from the wrapper div's perspective.
    const buttonWrapper = this.element.parentElement

    if (!buttonWrapper) {
      console.log('No parent wrapper found for button')
      return null
    }

    // Strategy 1: Look for the previous sibling element of the wrapper that contains a chart
    let element = buttonWrapper.previousElementSibling

    while (element) {
      // Check if this element contains a chart
      const chart = element.querySelector('[data-controller="chart"]')
      if (chart) {
        console.log('Found chart in previous sibling of wrapper:', chart)
        return chart
      }

      // Check if this element itself is a chart
      if (element.dataset.controller === 'chart') {
        console.log('Found chart as previous sibling of wrapper:', element)
        return element
      }

      element = element.previousElementSibling
    }

    // Strategy 2: Look for the next sibling element of the wrapper that contains a chart
    element = buttonWrapper.nextElementSibling

    while (element) {
      const chart = element.querySelector('[data-controller="chart"]')
      if (chart) {
        console.log('Found chart in next sibling of wrapper:', chart)
        return chart
      }

      if (element.dataset.controller === 'chart') {
        console.log('Found chart as next sibling of wrapper:', element)
        return element
      }

      element = element.nextElementSibling
    }

    // Strategy 3: No chart found
    console.log('No chart found near button wrapper')
    return null
  }

  extractChartData(canvas) {
    try {
      const labels = JSON.parse(canvas.dataset.chartLabelsValue || '[]')
      const datasets = JSON.parse(canvas.dataset.chartDatasetsValue || '[]')
      const chartType = canvas.dataset.chartTypeValue || 'unknown'

      // Get chart title if available
      const titleElement = this.element.closest('.bg-white')?.querySelector('h2')
      const title = titleElement?.textContent?.trim() || 'Chart Data'

      return {
        title,
        type: chartType,
        labels,
        datasets
      }
    } catch (error) {
      console.error('Error extracting chart data:', error)
      return null
    }
  }

  downloadCSV(chartData) {
    let csv = ''

    // Add metadata if enabled
    if (this.includeMetadataValue) {
      csv += `"Titel","${chartData.title}"\n`
      csv += `"Diagrammtyp","${chartData.type}"\n`
      csv += `"Exportdatum","${new Date().toLocaleDateString('de-DE')}"\n`
      csv += '\n'
    }

    // Add headers
    csv += '"Label"'
    chartData.datasets.forEach(dataset => {
      csv += `,"${dataset.label || 'Wert'}"`
    })
    csv += '\n'

    // Add data rows
    chartData.labels.forEach((label, index) => {
      csv += `"${label}"`
      chartData.datasets.forEach(dataset => {
        const value = dataset.data[index] || ''
        csv += `,${value}`
      })
      csv += '\n'
    })

    // Create and download file
    this.triggerDownload(csv, `${this.filenameValue}.csv`, 'text/csv;charset=utf-8;')
  }

  downloadJSON(chartData) {
    const jsonData = {
      metadata: this.includeMetadataValue ? {
        title: chartData.title,
        type: chartData.type,
        exportDate: new Date().toISOString(),
        exportDateFormatted: new Date().toLocaleDateString('de-DE')
      } : undefined,
      labels: chartData.labels,
      datasets: chartData.datasets
    }

    const jsonString = JSON.stringify(jsonData, null, 2)
    this.triggerDownload(jsonString, `${this.filenameValue}.json`, 'application/json;charset=utf-8;')
  }

  triggerDownload(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}
